/**
 * Central i18n / locale configuration for Places multi-region.
 *
 * This module is the ONLY source of truth for:
 *  - supported locales (BCP-47 codes)
 *  - short codes used by the legacy `useLang()` context (pt / en / es)
 *  - URL prefixes (pt-br, pt-pt, es-py)
 *  - market codes (BR / PT / PY) — sent to CRM
 *  - localized page-slug maps (route equivalence used by hreflang/switcher)
 *
 * IMPORTANT: does NOT remove or replace any existing i18n logic — only
 * augments the current `useLang()` provider. Legacy pages that call
 * `useLang().lang` still receive "pt" / "en" / "es".
 */

export const LOCALES = [
  { code: "pt-BR", short: "pt", prefix: "pt-br", market: "BR", country: "Brasil",   country_es: "Brasil",   country_en: "Brazil",   flag: "🇧🇷", ogLocale: "pt_BR", htmlLang: "pt-BR" },
  { code: "pt-PT", short: "pt", prefix: "pt-pt", market: "PT", country: "Portugal", country_es: "Portugal", country_en: "Portugal", flag: "🇵🇹", ogLocale: "pt_PT", htmlLang: "pt-PT" },
  { code: "es-PY", short: "es", prefix: "es-py", market: "PY", country: "Paraguay", country_es: "Paraguay", country_en: "Paraguay", flag: "🇵🇾", ogLocale: "es_PY", htmlLang: "es-PY" },
  // English is a "language-only" locale — no market-specific URL prefix. Content
  // rendering uses `translations.en.*` when present, else falls back to PT.
  // Kept last so findLocaleByShort("pt") still matches pt-BR first.
  { code: "en-US", short: "en", prefix: "en",    market: "INTL", country: "International", country_es: "Internacional", country_en: "International", flag: "🇺🇸", ogLocale: "en_US", htmlLang: "en" },
];

export const DEFAULT_LOCALE = LOCALES[0];        // pt-BR is the site default
export const LOCALE_CODES = LOCALES.map((l) => l.code);
export const LOCALE_PREFIXES = LOCALES.map((l) => l.prefix);

export function findLocaleByPrefix(prefix) {
  return LOCALES.find((l) => l.prefix === (prefix || "").toLowerCase()) || null;
}

export function findLocaleByShort(short) {
  // Ambiguous: pt-BR and pt-PT both share short "pt" — the caller usually
  // has a URL prefix to disambiguate; we return the FIRST match for legacy
  // useLang() fallbacks.
  return LOCALES.find((l) => l.short === short) || DEFAULT_LOCALE;
}

/**
 * Localized page slugs. Each key is a "route id" (locale-agnostic), values map
 * a locale prefix to the actual URL segment used in that market.
 *
 * Missing entries fall back to the DEFAULT_LOCALE variant.
 */
export const ROUTE_MAP = {
  home:         { "pt-br": "",                                  "pt-pt": "",                                             "es-py": "",                                   "en": "" },
  sobre:        { "pt-br": "sobre",                             "pt-pt": "sobre",                                        "es-py": "sobre",                              "en": "about" },
  servicos:     { "pt-br": "servicos",                          "pt-pt": "servicos",                                     "es-py": "servicios",                          "en": "services" },
  branding:     { "pt-br": "branding-imobiliario",              "pt-pt": "branding-imobiliario",                         "es-py": "branding-inmobiliario",              "en": "real-estate-branding" },
  marketing:    { "pt-br": "marketing-para-incorporadoras",     "pt-pt": "marketing-para-promotores-imobiliarios",       "es-py": "marketing-para-desarrolladoras",     "en": "marketing-for-developers" },
  campanhas:    { "pt-br": "campanhas-imobiliarias",            "pt-pt": "campanhas-imobiliarias",                       "es-py": "campanas-inmobiliarias",             "en": "real-estate-campaigns" },
  cases:        { "pt-br": "cases",                             "pt-pt": "projetos",                                     "es-py": "casos",                              "en": "cases" },
  insights:     { "pt-br": "insights",                          "pt-pt": "insights",                                     "es-py": "insights",                           "en": "insights" },
  carreiras:    { "pt-br": "carreiras",                         "pt-pt": "carreiras",                                    "es-py": "carreras",                           "en": "careers" },
  contato:      { "pt-br": "contato",                           "pt-pt": "contacto",                                     "es-py": "contacto",                           "en": "contact" },
  timeline38:   { "pt-br": "38-anos",                           "pt-pt": "38-anos",                                      "es-py": "38-anos",                            "en": "38-years" },
  privacidade:  { "pt-br": "privacidade",                       "pt-pt": "privacidade",                                  "es-py": "privacidad",                         "en": "privacy" },
};

/** Return the URL path for a given route+locale, e.g. localizedPath('cases', 'pt-pt') → '/pt-pt/projetos' */
export function localizedPath(routeId, prefix, extra = "") {
  const map = ROUTE_MAP[routeId];
  if (!map) return `/${prefix}${extra ? "/" + extra.replace(/^\/+/, "") : ""}`;
  const slug = map[prefix] ?? map[DEFAULT_LOCALE.prefix] ?? "";
  const clean = extra ? "/" + extra.replace(/^\/+/, "") : "";
  return slug ? `/${prefix}/${slug}${clean}` : `/${prefix}${clean}`;
}

/**
 * Given a full pathname (e.g. "/pt-pt/projetos/nome"), return the locale
 * object it belongs to, or null if none matches.
 */
export function detectLocaleFromPath(pathname = "/") {
  const first = pathname.replace(/^\/+/, "").split("/")[0].toLowerCase();
  return findLocaleByPrefix(first);
}

/**
 * Given an absolute site path (e.g. "/pt-br/cases/level-home-resort") return
 * the equivalent path in another locale, preserving trailing dynamic segments
 * (slugs) unchanged. Used by the header language selector so switching PT→EN
 * navigates to /en/cases/level-home-resort instead of leaving the visitor on a
 * PT URL where the URL-driven sync effect would immediately snap them back to PT.
 */
export function translatePath(pathname = "/", targetPrefix) {
  const target = findLocaleByPrefix(targetPrefix);
  if (!target) return pathname;
  const parts = pathname.replace(/^\/+/, "").split("/");
  const first = parts[0]?.toLowerCase();
  const current = findLocaleByPrefix(first);
  // Strip current locale prefix + primary segment (page slug)
  const rest = current ? parts.slice(1) : parts;
  if (rest.length === 0) return `/${target.prefix}`;
  const [pageSlug, ...tail] = rest;
  // Find the routeId whose slug matches the current pageSlug for the current locale.
  const currentPrefix = current?.prefix || DEFAULT_LOCALE.prefix;
  const routeId = Object.keys(ROUTE_MAP).find((id) => ROUTE_MAP[id][currentPrefix] === pageSlug)
                || Object.keys(ROUTE_MAP).find((id) => ROUTE_MAP[id][DEFAULT_LOCALE.prefix] === pageSlug);
  const nextSlug = routeId ? (ROUTE_MAP[routeId][target.prefix] ?? ROUTE_MAP[routeId][DEFAULT_LOCALE.prefix] ?? pageSlug) : pageSlug;
  const tailPath = tail.length ? `/${tail.join("/")}` : "";
  return nextSlug ? `/${target.prefix}/${nextSlug}${tailPath}` : `/${target.prefix}`;
}


export function buildAlternates(routeId, extra = "") {
  const out = {};
  LOCALES.forEach((l) => { out[l.code] = localizedPath(routeId, l.prefix, extra); });
  return out;
}

/** Absolute URL helper — uses PUBLIC_SITE_URL env or falls back to window.location.origin. */
export function absoluteUrl(path = "/") {
  const base = (typeof window !== "undefined" ? window.location.origin : "") || process.env.REACT_APP_PUBLIC_SITE_URL || "";
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}
